import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Gap, Icon, Link, Spacing, Text, TextCollapse, ToggleSwitch } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import LocalizedCurrency from 'components/LocalizedCurrency';
import DisclaimerText from 'components/purchase/DisclaimerText';
import { formatCoverageAmount, safeParsePrice } from 'utils/currency';
import './AncillariesSwitch.scss';

/**
 * Component to render multiple links based on object URL
 * @param {*} href Array of url object to show as links
 * @returns { array } Link component
 */
const MultiLink = ({ href }) => {
  return href.map(({ text, url }) => (
    <Link text={text} href={url} type="primary" newTab size="S" />
  ));
};

/**
 * hasDecimals function.
 * This function is used to determine if a price has decimals.
 * @param {number} price - Price to be displayed
 * @param {boolean} forceDecimals - If true, always return 2 decimals
 * @returns {number} - Number of decimals
 */
const hasDecimals = (price, forceDecimals = false) => {
  if (forceDecimals) return 2;

  return price % 1 === 0 ? 0 : 2;
};

/**
 * AncillariesSwitch component.
 * This component is used to display a switch with a title, icon, message, link, price, and a toggle switch.
 * @param {object} props - checked, isLoading, onChange, id, title, icon, message, linkText, href, price, singlePrice, plusTaxes, isDoters, disabled, headerTitle, headerIcon, children, fullPrice, coverageItems, disclaimerConfig
 * @param {boolean} props.checked - Whether the switch is checked
 * @param {boolean} props.isLoading - Whether the switch is loading
 * @param {function} props.onChange - Function to be called when the switch is changed
 * @param {string} props.id - Id of the switch
 * @param {string} props.title - Title of the switch
 * @param {string} props.icon - Icon of the switch
 * @param {string} props.message - Message of the switch
 * @param {string} props.linkText - Text of the link
 * @param {string|Array<{text: string, url: string}>} props.href - Href of the link or array of links
 * @param {number} props.price - Price of the switch
 * @param {boolean} props.singlePrice - Whether the switch is single price
 * @param {boolean} props.plusTaxes - Whether the switch has plus taxes
 * @param {boolean} props.isDoters - Whether the switch is doters
 * @param {boolean} props.disabled - Whether the switch is disabled
 * @param {string} props.headerTitle - Title of the header
 * @param {string} props.headerIcon - Icon of the header
 * @param {node} props.children - Children of the switch
 * @param {number} props.fullPrice - Full price of the switch
 * @param {Array<{key: string, amount: number|string, currency: string}>} props.coverageItems - Coverage items to display
 * @param {string|object} props.disclaimerConfig - Disclaimer configuration (string or {text, linkText, linkUrl})}
 * @param {string} props.taxRate - Tax rate for insurance (0 means no taxes)
 * @returns {JSX.Element} - AncillariesSwitch component
 */
const AncillariesSwitch = ({
  checked,
  isLoading,
  onChange,
  id,
  title,
  icon,
  message,
  linkText,
  href,
  price,
  singlePrice,
  plusTaxes,
  isDoters,
  disabled,
  headerTitle,
  headerIcon,
  children,
  fullPrice,
  coverageItems,
  disclaimerConfig,
  taxRate,
}) => {
  const { features } = useSelector((state) => state.whitelabelConfig);
  const { FUNNEL_STYLE, SHOW_INSURANCE_AMOUNT_WITH_TAXES, SHOW_INSURANCE_DECIMALS } = features;
  const isFlat = FUNNEL_STYLE === 'FLAT';
  const { t } = useTranslation();

  const insurancePrice = safeParsePrice(
    SHOW_INSURANCE_AMOUNT_WITH_TAXES && id === 'insurance' && fullPrice ? fullPrice : price,
  );

  return (
    <div
      className={`ancillaries-grid fadeinup ${isFlat && 'ancillaries-grid-flat'}  ${
        isDoters && 'ancillaries-grid-container-doters'
      }`}
    >
      {headerTitle && (
        <div className="ancillaries-grid-header">
          {headerIcon && <Icon type={headerIcon} size="S" />}
          <Text weight="semibold" size="M" color="white">
            {headerTitle}
          </Text>
        </div>
      )}
      <div className="ancillaries-grid-wrapper">
        <Spacing justifyContent="space-between">
          <Spacing justifyContent="space-between">
            <Icon type={icon} size="L" />
            <Spacing size="XS" vertical alignItems="flex-start">
              <Text weight="semibold" size="M">
                {title}
              </Text>
              {children ? (
                <>{children}</>
              ) : (
                <TextCollapse
                  triggerTextCollapsed={t('see_details')}
                  triggerTextExpanded={t('close_details')}
                  triggerOptions={{ size: 'S', type: 'discount' }}
                  margin="XS"
                  triggerPosition="top"
                  hasChevron
                >
                  <Spacing vertical size="XS" alignItems="flex-start">
                    {coverageItems && coverageItems.length > 0 ? (
                      coverageItems.map((item) => (
                        <Spacing
                          key={item.id ? `${item.key}-${item.id}` : item.key}
                          alignItems="center"
                          size="XS"
                        >
                          <Text size="S">•</Text>
                          <Text size="S">
                            {t(`insurance:${item.key}`)}: {formatCoverageAmount(item.amount)}
                          </Text>
                        </Spacing>
                      ))
                    ) : (
                      <Text size="S">{message}</Text>
                    )}
                    {typeof href === 'string' ? (
                      <Link text={linkText} href={href} type="primary" newTab size="S" />
                    ) : (
                      <MultiLink href={href} />
                    )}
                    {disclaimerConfig && (
                      <div className="ancillaries-disclaimer">
                        <Text size="XS" color="grayMedium">
                          <DisclaimerText config={disclaimerConfig} />
                        </Text>
                      </div>
                    )}
                  </Spacing>
                </TextCollapse>
              )}
            </Spacing>
          </Spacing>
          <Spacing vertical size="XS" alignItems="flex-end">
            <Gap size="S" alignItems="baseline">
              {insurancePrice > 0 && (
                <LocalizedCurrency
                  color="primary"
                  weight="semibold"
                  price={insurancePrice}
                  size="L"
                  decimals={hasDecimals(insurancePrice, SHOW_INSURANCE_DECIMALS)}
                  currencyStyle="currency"
                />
              )}
              {SHOW_INSURANCE_AMOUNT_WITH_TAXES && id === 'insurance' ? (
                <>
                  <Text size="XS" color="grayMedium">
                    {t('each_abbr')}
                  </Text>
                  <Text size="XS" color="grayMedium">
                    ({t('purchase:extra_price')} {t('purchase:included')})
                  </Text>
                </>
              ) : (
                <>
                  {plusTaxes && parseFloat(taxRate || '0') > 0 && (
                    <Text size="XS" color="grayMedium">
                      + {t('purchase:extra_price')}
                    </Text>
                  )}
                  {singlePrice && (
                    <Text size="XS" color="grayMedium">
                      {t('each_abbr')}
                    </Text>
                  )}
                </>
              )}
            </Gap>
            <ToggleSwitch
              color={isDoters ? 'loyalty' : 'accent'}
              checked={checked}
              isLoading={isLoading}
              id={id}
              onChange={!isLoading && onChange}
              fillBackground
              size="S"
              disabled={disabled}
            />
          </Spacing>
        </Spacing>
      </div>
    </div>
  );
};

AncillariesSwitch.propTypes = {
  checked: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  linkText: PropTypes.string.isRequired,
  href: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
  ]).isRequired,
  price: PropTypes.string.isRequired,
  singlePrice: PropTypes.bool,
  plusTaxes: PropTypes.bool,
  disabled: PropTypes.bool,
  isDoters: PropTypes.bool,
  headerTitle: PropTypes.string,
  headerIcon: PropTypes.string,
  children: PropTypes.node,
  fullPrice: PropTypes.string,
  coverageItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      currency: PropTypes.string,
    }),
  ),
  disclaimerConfig: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      linkText: PropTypes.string.isRequired,
      linkUrl: PropTypes.string.isRequired,
    }),
  ]),
  taxRate: PropTypes.string,
};

AncillariesSwitch.defaultProps = {
  singlePrice: false,
  plusTaxes: false,
  disabled: false,
  fullPrice: null,
  coverageItems: null,
  disclaimerConfig: null,
  taxRate: '0',
};

export default AncillariesSwitch;
